import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import {
  CardAvatar,
  CardHeader,
  VerticalSpace,
  Button,
} from '@brokerbay/design-components';
import { BankFilled } from '@ant-design/icons';

import BrokerageSelect from './BrokerageSelect';
import OnboardingProgress from './OnboardingProgress';
import OrgInfoPanel from './OrgInfoPanel';
import AlreadyClaimedAlert from './AlreadyClaimedAlert';
import AgreeToTermsCheckbox from './AgreeToTermsCheckbox';
import { useOrgOnboardingSelectContext } from '../../../contexts/OrgOnboardingSelectContext';

export type FindBrokerageViewProps = {
  handleGoBackStep: () => void;
  handleGoNextStep: (hash: string) => () => void;
  handleGoContactStep: () => void;
};

export const FindBrokerageView = (props: FindBrokerageViewProps) => {
  const { handleGoBackStep, handleGoNextStep, handleGoContactStep } = props;

  const { portalConfig, selectedOrg } = useOrgOnboardingSelectContext();

  const [agreedToTerms, setAgreedToTerms] = useState<boolean>(false);
  useEffect(() => {
    // Reset agree checkmark if user goes back to reselect org
    if (!selectedOrg) setAgreedToTerms(false);
  }, [selectedOrg]);

  const isClaimed = useMemo(() => {
    if (!selectedOrg) return false;
    const org = portalConfig?.orgs.find(o => o.id === selectedOrg);
    if (!org) return false;
    return org.isClaimed;
  }, [portalConfig, selectedOrg]);

  return (
    <VerticalSpace>
      <CardHeader
        title="Find your brokerage"
        icon={<StyledCardAvatar icon={BankFilled} />}
        onRequestStepBack={handleGoBackStep}
      />
      {!selectedOrg && <BrokerageSelect />}
      {selectedOrg && isClaimed && <AlreadyClaimedAlert />}
      {selectedOrg && <OrgInfoPanel />}
      {selectedOrg && !isClaimed && (
        <AgreeToTermsCheckbox
          value={agreedToTerms}
          onChange={setAgreedToTerms}
        />
      )}
      <Button
        type="primary"
        block
        onClick={handleGoNextStep(
          portalConfig?.orgs.find(o => o.id === selectedOrg)?.claimHash,
        )}
        data-cy="button-next-step"
        disabled={!agreedToTerms}
        hidden={isClaimed}
      >
        Next
      </Button>
      <Button
        type="link"
        block
        data-cy="button-contact-sales"
        onClick={handleGoContactStep}
      >
        I can&apos;t find my brokerage
      </Button>
      <OnboardingProgress step={1} />
    </VerticalSpace>
  );
};

const StyledCardAvatar = styled(CardAvatar)`
  & .anticon {
    vertical-align: top;
  }
`;
